import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Loader } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const ProcessingStatus = ({ steps, currentStep, progress = 0 }) => {
  const { darkMode } = useTheme();

  // Calculate progress percentage based on current step and overall progress
  const stepProgress = Math.min(100, Math.max(0, progress));

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="inline-block mb-4"
        >
          <Loader className="w-12 h-12 text-[#175e69]" />
        </motion.div>
        <h3 className={`text-xl font-semibold mb-2 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
          }`}>Processing Your Files</h3>
        <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>Please wait while we extract and structure your data</p>
        <div className="mt-4">
          <div className="text-2xl font-bold text-[#175e69]">{Math.round(stepProgress)}%</div>
          <div className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>Complete</div>
        </div>
      </div>

      <div className="space-y-4">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isPending = index > currentStep;

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex items-center space-x-4 p-4 rounded-lg transition-colors duration-300 ${isCompleted
                ? darkMode ? 'bg-green-900/20 border border-green-700/50' : 'bg-green-50 border border-green-200'
                : isCurrent
                  ? darkMode ? 'bg-[#175e69]/20 border border-[#175e69]/30' : 'bg-[#175e69]/10 border border-[#175e69]/20'
                  : darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'
                }`}
            >
              <div className="flex-shrink-0">
                {isCompleted ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : isCurrent ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Loader className="w-6 h-6 text-[#175e69]" />
                  </motion.div>
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                )}
              </div>

              <div className="flex-1">
                <p className={`font-medium transition-colors duration-300 ${isCompleted
                  ? darkMode ? 'text-green-300' : 'text-green-800'
                  : isCurrent
                    ? 'text-[#175e69]'
                    : darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  {step}
                </p>
              </div>

              <div className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                {isCompleted ? 'Completed' :
                  isCurrent ? 'In Progress' :
                    'Pending'}
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-8">
        <div className={`rounded-full h-3 transition-colors duration-300 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'
          }`}>
          <motion.div
            className="bg-gradient-to-r from-[#175e69] to-[#2a7c8a] h-3 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${stepProgress}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
        <div className={`flex justify-between text-sm mt-3 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
          <span>Step {currentStep + 1} of {steps.length}</span>
          <span>{Math.round(stepProgress)}% Complete</span>
        </div>
        <div className="text-center mt-2">
          <span className={`text-xs transition-colors duration-300 ${darkMode ? 'text-gray-500' : 'text-gray-500'
            }`}>
            {stepProgress === 100 ? 'Processing complete! Redirecting to results...' :
              currentStep < steps.length ? steps[currentStep] : 'Processing complete!'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProcessingStatus;

